import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { LinkedInProfile } from '@/hooks/useLinkedInProfiles';

interface LinkedInAnalyticsSectionProps {
  profiles: LinkedInProfile[];
  onFilterChange?: (filterType: string, filterValue: string) => void;
}

interface AnalyticsData {
  name: string;
  totalProfiles: number;
  averageConnections: number;
  averageMessages: number;
  dailyCapacity: number;
  totalConnections: number;
  totalMessages: number;
}

const getBarColor = (averageConnections: number) => {
  if (averageConnections >= 40) return '#10b981'; // green
  if (averageConnections >= 25) return '#f59e0b'; // yellow
  if (averageConnections > 0) return '#ef4444'; // red
  return '#6b7280'; // gray
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-medium text-gray-900">{label}</p>
        <p className="text-sm text-gray-600">Profiles: {data.totalProfiles}</p>
        <p className="text-sm text-gray-600">Daily Capacity: {data.dailyCapacity}</p>
        <p className="text-sm text-gray-600">Total Connections: {data.totalConnections.toLocaleString()}</p>
        <p className="text-sm text-gray-600">Total Messages: {data.totalMessages.toLocaleString()}</p>
        <p className="text-sm text-gray-600">Avg Connections: {data.averageConnections}</p>
      </div>
    );
  }
  return null;
};

const AnalyticsChart = ({ data, title, onBarClick }: { data: AnalyticsData[]; title: string; onBarClick?: (name: string) => void }) => {
  if (!data || data.length === 0) {
    return (
      <div className="space-y-2">
        <h4 className="text-base font-semibold text-foreground">{title}</h4>
        <div className="flex items-center justify-center h-32 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <div className="text-center">
            <BarChart3 className="h-8 w-8 text-gray-400 mx-auto mb-1" />
            <p className="text-gray-500 text-sm font-medium">No data available</p>
            <p className="text-gray-400 text-xs">Add LinkedIn profiles to see analytics</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      <h4 className="text-base font-semibold text-foreground mb-1 mt-3 pt-3 border-t border-gray-200">{title}</h4>
      <div className="h-80 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 80,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="name" 
              angle={-45}
              textAnchor="end"
              height={100}
              fontSize={12}
            />
            <YAxis 
              fontSize={12}
              label={{ value: 'Avg Daily Connections', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar 
              dataKey="averageConnections" 
              radius={[4, 4, 0, 0]}
              onClick={(data) => onBarClick && onBarClick(data.name)}
              style={{ cursor: onBarClick ? 'pointer' : 'default' }}
              name="Average Connections"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getBarColor(entry.averageConnections)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      {/* Data Summary Table */}
      <div className="rounded-lg p-3 border bg-muted/50 w-full h-80">
        <div className="grid grid-cols-2 gap-3 h-full overflow-y-auto">
          {data.slice(0, 6).map((item, index) => (
            <div key={index} className="bg-card rounded-lg p-3 border hover:bg-accent/50 transition-colors cursor-pointer"
                 onClick={() => onBarClick && onBarClick(item.name)}>
              <div className="text-sm font-semibold text-foreground truncate mb-2" title={item.name}>
                {item.name}
              </div>
              
              {/* Average Connections - Prominent */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground font-medium">Avg Connections</span>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <span className="text-sm font-bold text-blue-600 dark:text-blue-400">{item.averageConnections}</span>
                </div>
              </div>
              
              {/* Average Messages */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground font-medium">Avg Messages</span>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                  <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{item.averageMessages}</span>
                </div>
              </div>
              
              {/* Profile Count */}
              <div className="text-xs text-muted-foreground text-center bg-muted rounded px-2 py-1">
                {item.totalProfiles} profile{item.totalProfiles !== 1 ? 's' : ''}
              </div>
            </div>
          ))}
        </div>
        {data.length > 6 && (
          <div className="text-center mt-3">
            <span className="text-xs text-muted-foreground bg-card px-2 py-1 rounded border">
              + {data.length - 6} more entries • Click chart bars or boxes to filter
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export function LinkedInAnalyticsSection({ profiles, onFilterChange }: LinkedInAnalyticsSectionProps) {
  const [isOpen, setIsOpen] = useState(false);

  const clientAnalytics = useMemo(() => {
    const clientMap = new Map<string, { profiles: number; connections: number; messages: number; inmails: number; capacity: number }>();

    profiles.forEach(profile => {
      const client = profile.client_name || 'No Client';
      
      if (!clientMap.has(client)) {
        clientMap.set(client, { profiles: 0, connections: 0, messages: 0, inmails: 0, capacity: 0 });
      }

      const current = clientMap.get(client)!;
      current.profiles += 1;
      current.connections += profile.connections_accepted || 0;
      current.messages += profile.direct_messages_sent || 0;
      current.inmails += profile.inmails_sent || 0;
      current.capacity += profile.daily_connections || 0;
    });

    return Array.from(clientMap.entries()).map(([name, data]) => ({
      name,
      totalProfiles: data.profiles,
      averageConnections: data.profiles > 0 ? Number((data.capacity / data.profiles).toFixed(1)) : 0,
      averageMessages: data.profiles > 0 ? Number(((data.messages + data.inmails) / data.profiles).toFixed(1)) : 0,
      dailyCapacity: data.capacity,
      totalConnections: data.connections,
      totalMessages: data.messages + data.inmails,
    })).sort((a, b) => b.averageConnections - a.averageConnections);
  }, [profiles]);

  const statusAnalytics = useMemo(() => {
    const statusMap = new Map<string, { profiles: number; connections: number; messages: number; inmails: number; capacity: number }>();

    profiles.forEach(profile => {
      const status = profile.platform_status || 'unknown';
      
      if (!statusMap.has(status)) {
        statusMap.set(status, { profiles: 0, connections: 0, messages: 0, inmails: 0, capacity: 0 });
      }

      const current = statusMap.get(status)!;
      current.profiles += 1;
      current.connections += profile.connections_accepted || 0;
      current.messages += profile.direct_messages_sent || 0;
      current.inmails += profile.inmails_sent || 0;
      current.capacity += profile.daily_connections || 0;
    });

    return Array.from(statusMap.entries()).map(([name, data]) => ({
      name,
      totalProfiles: data.profiles,
      averageConnections: data.profiles > 0 ? Number((data.capacity / data.profiles).toFixed(1)) : 0,
      averageMessages: data.profiles > 0 ? Number(((data.messages + data.inmails) / data.profiles).toFixed(1)) : 0,
      dailyCapacity: data.capacity,
      totalConnections: data.connections,
      totalMessages: data.messages + data.inmails,
    })).sort((a, b) => b.averageConnections - a.averageConnections);
  }, [profiles]);

  return (
    <Card className="w-full">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <BarChart3 className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">LinkedIn Analytics by Client</CardTitle>
              </div>
              <Button variant="ghost" size="sm">
                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-2">
            <div className="text-sm text-muted-foreground">
              Analyze LinkedIn profile performance across different clients and platform statuses.
            </div>
            
            {/* 2-Column Grid Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <AnalyticsChart 
                data={clientAnalytics} 
                title="Performance by Client"
                onBarClick={(name) => onFilterChange && onFilterChange('client_name', name)}
              />
              
              <AnalyticsChart 
                data={statusAnalytics} 
                title="Performance by Platform Status"
                onBarClick={(name) => onFilterChange && onFilterChange('platform_status', name)}
              />
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}